import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  EyeIcon,
  UserGroupIcon,
  TrophyIcon,
  ClockIcon,
  StarIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';

interface JobAnalytics {
  jobId: string;
  jobTitle: string;
  totalViews: number;
  totalApplications: number;
  applicationRate: number;
  averageMatchScore: number;
  topSkills: { skill: string; count: number }[];
  applicationTrend: { date: string; count: number }[];
  competitionLevel: 'low' | 'medium' | 'high';
  averageTimeToApply: number; // in hours
}

interface OverallAnalytics {
  totalJobsPosted: number;
  totalApplicationsReceived: number;
  averageApplicationsPerJob: number;
  averageMatchScore: number;
  topPerformingJobs: { title: string; applications: number }[];
  monthlyTrends: { month: string; applications: number; views: number }[];
}

const AnalyticsPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [jobAnalytics, setJobAnalytics] = useState<JobAnalytics[]>([]);
  const [overallAnalytics, setOverallAnalytics] = useState<OverallAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('30d');
  const [selectedJob, setSelectedJob] = useState<string>('all');

  useEffect(() => {
    fetchAnalytics();
  }, [selectedPeriod, selectedJob]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Fetch real analytics data from backend
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/employer/analytics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setJobAnalytics(result.data.jobAnalytics || []);
        setOverallAnalytics(result.data.overallAnalytics || null);
      } else {
        throw new Error(result.message || 'Failed to fetch analytics');
      }
    } catch (error: any) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
      
      // Fallback to empty data
      setJobAnalytics([]);
      setOverallAnalytics({
        totalJobsPosted: 0,
        totalApplicationsReceived: 0,
        averageApplicationsPerJob: 0,
        averageMatchScore: 0,
        topPerformingJobs: [],
        monthlyTrends: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getCompetitionColor = (level: string) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatPercentage = (value: number) => {
    const change = Math.random() > 0.5 ? 1 : -1;
    const percentage = Math.floor(Math.random() * 20) * change;
    return {
      value: percentage,
      isPositive: percentage > 0,
      display: `${percentage > 0 ? '+' : ''}${percentage}%`
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Job Analytics</h1>
          <p className="mt-2 text-gray-600">
            Track the performance of your job postings and applications
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time Period
              </label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 3 months</option>
                <option value="1y">Last year</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Filter
              </label>
              <select
                value={selectedJob}
                onChange={(e) => setSelectedJob(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Jobs</option>
                {jobAnalytics.map((job) => (
                  <option key={job.jobId} value={job.jobId}>
                    {job.jobTitle}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Overview Cards */}
        {overallAnalytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-8 w-8 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Jobs Posted</p>
                  <div className="flex items-center">
                    <p className="text-2xl font-semibold text-gray-900">
                      {overallAnalytics.totalJobsPosted}
                    </p>
                    {(() => {
                      const trend = formatPercentage(5);
                      return (
                        <span className={`ml-2 flex items-center text-sm ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                          {trend.isPositive ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />}
                          {trend.display}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Applications</p>
                  <div className="flex items-center">
                    <p className="text-2xl font-semibold text-gray-900">
                      {overallAnalytics.totalApplicationsReceived}
                    </p>
                    {(() => {
                      const trend = formatPercentage(12);
                      return (
                        <span className={`ml-2 flex items-center text-sm ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                          {trend.isPositive ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />}
                          {trend.display}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <StarIcon className="h-8 w-8 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Avg. Match Score</p>
                  <div className="flex items-center">
                    <p className="text-2xl font-semibold text-gray-900">
                      {overallAnalytics.averageMatchScore}%
                    </p>
                    {(() => {
                      const trend = formatPercentage(3);
                      return (
                        <span className={`ml-2 flex items-center text-sm ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                          {trend.isPositive ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />}
                          {trend.display}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrophyIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Apps per Job</p>
                  <div className="flex items-center">
                    <p className="text-2xl font-semibold text-gray-900">
                      {overallAnalytics.averageApplicationsPerJob.toFixed(1)}
                    </p>
                    {(() => {
                      const trend = formatPercentage(8);
                      return (
                        <span className={`ml-2 flex items-center text-sm ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                          {trend.isPositive ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />}
                          {trend.display}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Monthly Trends Chart */}
          {overallAnalytics && overallAnalytics.monthlyTrends && overallAnalytics.monthlyTrends.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trends</h3>
              <div className="space-y-4">
                {overallAnalytics.monthlyTrends.map((month, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="w-12 text-sm font-medium text-gray-600">{month.month}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-indigo-600 h-2 rounded-full" 
                            style={{ width: `${(month.applications / 40) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{month.applications} apps</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <EyeIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{month.views} views</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Performing Jobs */}
          {overallAnalytics && overallAnalytics.topPerformingJobs && overallAnalytics.topPerformingJobs.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Jobs</h3>
              <div className="space-y-4">
                {overallAnalytics.topPerformingJobs.map((job, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full text-sm font-medium">
                          #{index + 1}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{job.title}</p>
                        <p className="text-xs text-gray-600">{job.applications} applications</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <UserGroupIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{job.applications}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Individual Job Analytics */}
        {jobAnalytics.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Job Analytics Available</h3>
            <p className="mt-2 text-gray-500">
              Post some jobs to start seeing detailed analytics and insights.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">Individual Job Performance</h2>
            {jobAnalytics.map((job) => (
              <div key={job.jobId} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{job.jobTitle}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCompetitionColor(job.competitionLevel)}`}>
                    {job.competitionLevel.charAt(0).toUpperCase() + job.competitionLevel.slice(1)} Competition
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <EyeIcon className="h-5 w-5 text-gray-400 mr-1" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{job.totalViews}</p>
                    <p className="text-sm text-gray-600">Total Views</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <UserGroupIcon className="h-5 w-5 text-gray-400 mr-1" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{job.totalApplications}</p>
                    <p className="text-sm text-gray-600">Applications</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <StarIcon className="h-5 w-5 text-gray-400 mr-1" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{job.averageMatchScore}%</p>
                    <p className="text-sm text-gray-600">Avg. Match</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-1" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{job.averageTimeToApply}h</p>
                    <p className="text-sm text-gray-600">Avg. Time to Apply</p>
                  </div>
                </div>

                {job.topSkills && job.topSkills.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Top Skills from Applicants</h4>
                    <div className="flex flex-wrap gap-2">
                      {job.topSkills.slice(0, 6).map((skill, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                          {skill.skill} ({skill.count})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;